import Tool, {
  type IToolActionInput,
  type IToolEnabledInput,
  simpleToolButton,
} from './Tool';
import {
  isSimpleSelection,
  hasConnectorAttachmentBehaviours,
} from '../objects/guards';
import Connector, { type Attachable } from '../objects/Connector';
import { AttachmentPoint, ConnectorEnd } from '../types';
import {
  calculateAttachmentPosition,
  allowsCenterAttachment,
} from '../utils/connectorAttachment';
import type WhiteboardController from '../controllers/WhiteboardController';
import { CONNECTOR_END_STYLE } from '../constants';
import connectShapesIcon from '../assets/icons/connectShapes.svg';
import { svgToIcon } from '../utils/svg';

class ToolConnectGroup extends Tool {
  static toolName = 'Connect objects';

  static action({ controller, selection }: IToolActionInput) {
    if (!isSimpleSelection(selection) || selection.size() < 2) {
      return;
    }

    const objects = selection.getObjects();

    // Filter to only objects that can have connectors attached AND support center attachment
    const attachableObjects = objects.filter(
      obj =>
        hasConnectorAttachmentBehaviours(obj) &&
        allowsCenterAttachment(obj as Attachable)
    ) as Attachable[];
    if (attachableObjects.length < 2) {
      return;
    }

    // Sort objects by their top-left position (x + y coordinate)
    const sortedObjects = attachableObjects
      .map(obj => ({
        obj,
        score: obj.getCenterPoint().x + obj.getCenterPoint().y,
      }))
      .sort((a, b) => a.score - b.score)
      .map(item => item.obj);

    // Create connectors in sequence from most top-left to most bottom-right
    const createdConnectors: Connector[] = [];
    for (let i = 0; i < sortedObjects.length - 1; i++) {
      const sourceObj = sortedObjects[i];
      const targetObj = sortedObjects[i + 1];

      // Check if connector already exists between these objects
      if (!this.hasExistingConnector(sourceObj, targetObj)) {
        const connector = this.createConnectorBetweenObjects(
          controller,
          sourceObj,
          targetObj
        );
        if (connector) {
          createdConnectors.push(connector);
        }
      }
    }

    this.toolActionComplete({ controller });
  }

  private static hasExistingConnector(
    obj1: Attachable,
    obj2: Attachable
  ): boolean {
    // Get all connectors attached to obj1
    const obj1Connectors = (obj1.connectorAttachments || []).map(
      attachment => attachment.connector
    );

    // Check if any of obj1's connectors are also connected to obj2
    return obj1Connectors.some(connector => {
      const obj2Connectors = (obj2.connectorAttachments || []).map(
        attachment => attachment.connector
      );
      return obj2Connectors.includes(connector);
    });
  }

  private static createConnectorBetweenObjects(
    controller: WhiteboardController,
    sourceObj: Attachable,
    targetObj: Attachable
  ): Connector | null {
    const sourceAttachment = AttachmentPoint.Center;
    const targetAttachment = AttachmentPoint.Center;

    const startPoint = calculateAttachmentPosition(
      sourceObj,
      sourceAttachment,
      { withStandoff: true }
    );
    const endPoint = calculateAttachmentPosition(targetObj, targetAttachment, {
      withStandoff: true,
    });

    if (!startPoint || !endPoint) {
      console.error('Could not calculate attachment positions');
      return null;
    }

    const connector = new Connector([startPoint, endPoint], {
      controller,
      ...controller.defaultsManager.for('connector'),
      endStyles: [CONNECTOR_END_STYLE.NONE, CONNECTOR_END_STYLE.FILLED_ARROW],
    });

    controller.addToCanvas(connector);

    sourceObj.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_START,
      sourceAttachment
    );
    targetObj.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_END,
      targetAttachment
    );

    // Update connector routing to ensure proper positioning
    connector.constrainConnector({});

    return connector;
  }

  static enabled({ selection }: IToolEnabledInput) {
    if (!selection || selection.isLocked || !isSimpleSelection(selection)) {
      return false;
    }

    // Count only objects that can have connectors attached AND support center attachment
    const attachableCount = selection
      .getObjects()
      .filter(
        obj =>
          hasConnectorAttachmentBehaviours(obj) &&
          allowsCenterAttachment(obj as Attachable)
      ).length;
    return attachableCount >= 2;
  }

  static buttonComponent() {
    return simpleToolButton(ToolConnectGroup, svgToIcon(connectShapesIcon));
  }
}

export default ToolConnectGroup;
